import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { allRemoteSettingsSelect } from '../../../redux/selectors';
import { money, notify } from '../../../utility/Helper1';
import { exchangeActions } from '../../../redux/actions';
import WrapUpP2p from './layouts/WrapUpP2p';

// MUI Components
import { 
  Box, 
  Typography, 
  Button, 
  Card, 
  CardContent, 
  Grid, 
  Select, 
  MenuItem, 
  CircularProgress,
  Container,
  Chip
} from '@mui/material';

// Lucide Icons
import { 
  ChevronsRight, 
  ArrowLeft, 
  ArrowRight, 
  TrendingUp, 
  CalendarClock, 
  Wallet, 
  Landmark, 
  Clock, 
  Check, 
  RefreshCw,
  DollarSign,
  Zap
} from 'lucide-react';

const NeonInvestmentFlow = () => {
  const site_settings = useSelector(allRemoteSettingsSelect);
  const currencies = site_settings?.plans;
  const [selectedCurrency, setSelectedCurrency] = useState(currencies?.[0]?.mother?.type);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [amount, setAmount] = useState(null);
  const [submittingInvestment, setSubmittingInvestment] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Predefined amounts in thousands
  const predefinedAmounts = [10000, 20000, 50000, 70000, 100000, 150000, 200000, 250000, 300000, 400000];

  const allPlans = site_settings?.plans?.filter(el => el.mother?.type === selectedCurrency);
  const selectedCurrencyObj = allPlans?.[0]?.mother;

  // Calculate projected returns based on p2p_roi
  const calculateReturns = () => {
    if (!selectedPlan || !amount) return { returns: 0, total: 0 };
    
    // Calculate returns for the investment period
    const investmentDays = parseInt(selectedPlan?.p2p_ghdate) || 30; // Default to 30 if not specified
    const periodRoi = parseFloat(selectedPlan?.p2p_roi ?? 0);
    
    const investmentAmount = parseFloat(amount);
    const returns = (investmentAmount * periodRoi / 100);
    const total = returns;
    
    return {
      returns,
      total,
      periodRoi: periodRoi?.toFixed(2),
      daysText: `${investmentDays} days (${(periodRoi)}% return)`
    };
  };

  const projectedReturns = calculateReturns();

  useEffect(() => {
    // Reset amount when plan changes
    if (selectedPlan) {
      setAmount(null);
    }
  }, [selectedPlan]);

  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
    setAmount(null);
  };

  const handleAmountSelect = (selectedAmount) => {
    setAmount(selectedAmount);
  };

  const isAmountValid = selectedPlan && amount && 
    parseFloat(amount) >= parseFloat(selectedPlan.p2p_minimumdonation) && 
    parseFloat(amount) <= parseFloat(selectedPlan.p2p_maximumdonation);

  const isAmountAvailable = (amountValue) => {
    if (!selectedPlan) return false;
    return parseFloat(amountValue) >= parseFloat(selectedPlan.p2p_minimumdonation) && 
           parseFloat(amountValue) <= parseFloat(selectedPlan.p2p_maximumdonation);
  };

  const submitInvestment = () => {
    setSubmittingInvestment(true);
    const data = {
      amount,
      plan: selectedPlan?.type
    };

    dispatch(exchangeActions.onPostExchange(data, "p2p/exchange/new", response => {
      setSubmittingInvestment(false);
      notify(response.msg, response.code);

      if(response?.code == 1){
        navigate('/list');
      }
    }));
  };

  const formatAmountDisplay = (value) => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(value % 1000000 === 0 ? 0 : 1)}M`;
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(value % 1000 === 0 ? 0 : 1)}K`;
    }
    return value.toString();
  };

  return (
    <WrapUpP2p>
      <Box 
        sx={{ 
          minHeight: '100vh', 
          background: 'linear-gradient(135deg, #050520 0%, #0F0F35 100%)',
          padding: { xs: 2, md: 5 },
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <Container maxWidth="lg">
          <Box 
            sx={{ 
              position: 'relative', 
              overflow: 'hidden',
              borderRadius: 4,
              background: 'rgba(15, 15, 40, 0.7)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}
          >
            {/* Glow effects */}
            <Box sx={{ 
              position: 'absolute', 
              width: '150px', 
              height: '150px', 
              borderRadius: '50%', 
              background: 'radial-gradient(circle, rgba(92, 233, 231, 0.4) 0%, rgba(92, 233, 231, 0) 70%)',
              top: '-50px',
              left: '-50px',
              zIndex: 0
            }} />
            <Box sx={{ 
              position: 'absolute', 
              width: '200px', 
              height: '200px', 
              borderRadius: '50%', 
              background: 'radial-gradient(circle, rgba(234, 108, 252, 0.4) 0%, rgba(234, 108, 252, 0) 70%)',
              bottom: '-80px',
              right: '-80px',
              zIndex: 0
            }} />

            <Box sx={{ position: 'relative', zIndex: 1, p: { xs: 3, md: 5 } }}>
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 700, 
                  mb: 3, 
                  textAlign: 'center',
                  background: 'linear-gradient(90deg, #5CE9E7 0%, #EA6CFC 100%)',
                  backgroundClip: 'text',
                  textFillColor: 'transparent',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  filter: 'drop-shadow(0 0 8px rgba(92, 233, 231, 0.3))'
                }}
              >
                Amplify Your Bid
              </Typography>

              {/* Currency Selection */}
              {currencies?.length > 1 && (
                <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center' }}>
                  <Select
                    value={selectedCurrency}
                    onChange={(e) => {
                      setSelectedCurrency(e.target.value);
                      setSelectedPlan(null);
                      setAmount(null);
                    }}
                    sx={{
                      width: '150px',
                      borderRadius: 2,
                      background: 'rgba(255, 255, 255, 0.1)',
                      color: 'white',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.2)',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(92, 233, 231, 0.5)',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#5CE9E7',
                      },
                      '& .MuiSelect-icon': {
                        color: '#EA6CFC',
                      }
                    }}
                  >
                    {currencies?.map(currency => (
                      <MenuItem key={currency?.mother?.abbrRo} value={currency?.mother?.type}>
                        {currency?.mother?.abbrRo}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              )}

              <Grid container spacing={3}>
                {/* Plan selection column */}
                <Grid item xs={12} md={6} sx={{ 
                  borderRight: { md: '1px solid rgba(255, 255, 255, 0.1)' }, 
                  pr: { md: 3 } 
                }}>
                  <Box sx={{ mb: 3 }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        color: 'white', 
                        mb: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      <Landmark size={20} /> Bid Plans
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      Select a plan that matches your bid goals
                    </Typography>
                  </Box>

                  {/* Investment Plans */}
                  <Box sx={{ mb: 3 }}>
                    {allPlans?.[0]?.childs?.map((plan) => (
                      <Card 
                        key={plan.type}
                        onClick={() => handlePlanSelect(plan)}
                        sx={{
                          mb: 2,
                          background: selectedPlan?.type === plan.type 
                            ? 'linear-gradient(120deg, rgba(92, 233, 231, 0.15) 0%, rgba(234, 108, 252, 0.15) 100%)'
                            : 'rgba(255, 255, 255, 0.05)',
                          border: selectedPlan?.type === plan.type 
                            ? '1px solid rgba(92, 233, 231, 0.5)'
                            : '1px solid rgba(255, 255, 255, 0.1)',
                          boxShadow: selectedPlan?.type === plan.type 
                            ? '0 0 15px rgba(92, 233, 231, 0.2)'
                            : 'none',
                          borderRadius: 2,
                          transition: 'all 0.3s ease',
                          cursor: 'pointer',
                          '&:hover': {
                            transform: 'translateY(-3px)',
                            boxShadow: '0 7px 20px rgba(92, 233, 231, 0.15)',
                            border: '1px solid rgba(92, 233, 231, 0.3)'
                          }
                        }}
                      >
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                              {plan.name}
                            </Typography>
                            <Box 
                              sx={{ 
                                borderRadius: 5, 
                                px: 1.5, 
                                py: 0.5, 
                                background: 'linear-gradient(90deg, #5CE9E7 0%, #EA6CFC 100%)',
                                display: 'flex',
                                alignItems: 'center'
                              }}
                            >
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#0A0A20' }}>
                                {plan.p2p_roi}% ROI
                              </Typography>
                            </Box>
                          </Box>

                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Wallet size={16} color="#5CE9E7" />
                                <Typography 
                                  variant="body2" 
                                  sx={{ ml: 1, color: 'rgba(255, 255, 255, 0.7)' }}
                                >
                                  Min: {money(plan?.p2p_minimumdonation, selectedCurrencyObj?.type)}
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6}>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Wallet size={16} color="#5CE9E7" />
                                <Typography 
                                  variant="body2" 
                                  sx={{ ml: 1, color: 'rgba(255, 255, 255, 0.7)' }}
                                >
                                  Max: {money(plan?.p2p_maximumdonation, selectedCurrencyObj?.type)}
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6}>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <CalendarClock size={16} color="#5CE9E7" />
                                <Typography 
                                  variant="body2" 
                                  sx={{ ml: 1, color: 'rgba(255, 255, 255, 0.7)' }}
                                >
                                  {plan.p2p_ghdate} days
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6}>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <TrendingUp size={16} color="#5CE9E7" />
                                <Typography 
                                  variant="body2" 
                                  sx={{ ml: 1, color: 'rgba(255, 255, 255, 0.7)' }}
                                >
                                  {plan.p2p_roi}% Return
                                </Typography>
                              </Box>
                            </Grid>
                          </Grid>

                          {selectedPlan?.type === plan.type && (
                            <Box 
                              sx={{ 
                                display: 'flex', 
                                justifyContent: 'center', 
                                mt: 2,
                                pt: 2,
                                borderTop: '1px solid rgba(255, 255, 255, 0.1)'
                              }}
                            >
                              <Check color="#5CE9E7" size={20} />
                              <Typography 
                                variant="body2" 
                                sx={{ ml: 1, color: '#5CE9E7', fontWeight: 500 }}
                              >
                                Selected
                              </Typography>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                </Grid>

                {/* Amount and confirmation column */}
                <Grid item xs={12} md={6} sx={{ pl: { md: 3 } }}>
                  {selectedPlan ? (
                    <>
                      <Box sx={{ mb: 3 }}>
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            color: 'white', 
                            mb: 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                          }}
                        >
                          <DollarSign size={20} /> Choose Bid Amount
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2 }}>
                          Select your preferred bid amount
                        </Typography>
                        <Box 
                          sx={{ 
                            display: 'inline-block',
                            px: 2, 
                            py: 0.5, 
                            borderRadius: 2,
                            background: 'linear-gradient(90deg, rgba(92, 233, 231, 0.2) 0%, rgba(234, 108, 252, 0.2) 100%)',
                            border: '1px solid rgba(92, 233, 231, 0.3)',
                          }}
                        >
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 500, 
                              color: '#EA6CFC',
                            }}
                          >
                            {selectedPlan.name} • {selectedPlan.p2p_roi}% ROI • {selectedPlan.p2p_ghdate} days
                          </Typography>
                        </Box>
                      </Box>

                      {/* Predefined Amount Selection */}
                      <Box sx={{ mb: 4 }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            mb: 2, 
                            color: 'white', 
                            fontWeight: 500, 
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Zap size={16} color="#EA6CFC" />
                            <span>Quick Select Amount</span>
                          </Box>
                          <span style={{ color: 'rgba(255, 255, 255, 0.7)', fontWeight: 400, fontSize: '0.75rem' }}>
                            Range: {money(selectedPlan.p2p_minimumdonation, selectedCurrencyObj?.type)} - {money(selectedPlan.p2p_maximumdonation, selectedCurrencyObj?.type)}
                          </span>
                        </Typography>

                        <Grid container spacing={1.5}>
                          {predefinedAmounts.map((amountValue) => {
                            const isAvailable = isAmountAvailable(amountValue);
                            const isSelected = amount === amountValue;
                            
                            return (
                              <Grid item xs={6} sm={4} key={amountValue}>
                                <Card
                                  onClick={() => isAvailable ? handleAmountSelect(amountValue) : null}
                                  sx={{
                                    background: isSelected 
                                      ? 'linear-gradient(135deg, rgba(92, 233, 231, 0.2) 0%, rgba(234, 108, 252, 0.2) 100%)'
                                      : isAvailable 
                                        ? 'rgba(255, 255, 255, 0.05)'
                                        : 'rgba(255, 255, 255, 0.02)',
                                    border: isSelected 
                                      ? '2px solid rgba(92, 233, 231, 0.6)'
                                      : isAvailable 
                                        ? '1px solid rgba(255, 255, 255, 0.1)'
                                        : '1px solid rgba(255, 255, 255, 0.05)',
                                    borderRadius: 2,
                                    cursor: isAvailable ? 'pointer' : 'not-allowed',
                                    transition: 'all 0.3s ease',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    opacity: isAvailable ? 1 : 0.4,
                                    '&:hover': isAvailable ? {
                                      transform: 'translateY(-2px)',
                                      border: '1px solid rgba(92, 233, 231, 0.4)',
                                      boxShadow: '0 4px 15px rgba(92, 233, 231, 0.15)'
                                    } : {}
                                  }}
                                >
                                  {isSelected && (
                                    <Box 
                                      sx={{ 
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        height: '3px',
                                        background: 'linear-gradient(90deg, #5CE9E7 0%, #EA6CFC 100%)'
                                      }} 
                                    />
                                  )}
                                  
                                  <CardContent sx={{ p: 2, textAlign: 'center' }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                      <Typography 
                                        variant="h6" 
                                        sx={{ 
                                          color: isSelected ? '#5CE9E7' : isAvailable ? 'white' : 'rgba(255, 255, 255, 0.5)',
                                          fontWeight: 600,
                                          mb: 0.5
                                        }}
                                      >
                                        {formatAmountDisplay(amountValue)}
                                      </Typography>
                                      <Typography 
                                        variant="body2" 
                                        sx={{ 
                                          color: isSelected ? 'rgba(92, 233, 231, 0.8)' : isAvailable ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.3)',
                                          fontSize: '0.75rem'
                                        }}
                                      >
                                        {money(amountValue, selectedCurrencyObj?.type)}
                                      </Typography>
                                      {isSelected && (
                                        <Box sx={{ mt: 1 }}>
                                          <Check size={16} color="#5CE9E7" />
                                        </Box>
                                      )}
                                      {!isAvailable && (
                                        <Chip 
                                          label="N/A" 
                                          size="small" 
                                          sx={{ 
                                            mt: 1, 
                                            height: '20px',
                                            fontSize: '0.6rem',
                                            background: 'rgba(255, 255, 255, 0.1)',
                                            color: 'rgba(255, 255, 255, 0.5)'
                                          }} 
                                        />
                                      )}
                                    </Box>
                                  </CardContent>
                                </Card>
                              </Grid>
                            );
                          })}
                        </Grid>
                      </Box>

                      {/* Projection Card */}
                      {amount && (
                        <Card sx={{ 
                          mb: 4, 
                          background: 'rgba(255, 255, 255, 0.05)',
                          backdropFilter: 'blur(10px)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          borderRadius: 3,
                          position: 'relative',
                          overflow: 'hidden'
                        }}>
                          {/* Animated gradient bar */}
                          <Box 
                            sx={{ 
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              width: '100%',
                              height: '4px',
                              background: 'linear-gradient(90deg, #5CE9E7 0%, #EA6CFC 100%)',
                              animation: 'pulse 2s infinite'
                            }} 
                          />
                          
                          <CardContent>
                            <Typography 
                              variant="subtitle1" 
                              sx={{ 
                                color: 'white', 
                                fontWeight: 600, 
                                mb: 2,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                              }}
                            >
                              <RefreshCw size={18} /> Projected Returns
                            </Typography>

                            <Grid container spacing={2}>
                              <Grid item xs={4}>
                                <Box sx={{ mb: 2 }}>
                                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                    Bid Period
                                  </Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                                    <Clock size={16} color="#5CE9E7" />
                                    <Typography variant="body1" sx={{ color: 'white', ml: 1, fontWeight: 500 }}>
                                      {selectedPlan.p2p_ghdate} days
                                    </Typography>
                                  </Box>
                                </Box>
                              </Grid>
                              <Grid item xs={4}>
                                <Box sx={{ mb: 2 }}>
                                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                    ROI Rate
                                  </Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                                    <TrendingUp size={16} color="#EA6CFC" />
                                    <Typography variant="body1" sx={{ color: 'white', ml: 1, fontWeight: 500 }}>
                                      {selectedPlan.p2p_roi}%
                                    </Typography>
                                  </Box>
                                </Box>
                              </Grid>
                              <Grid item xs={4}>
                                <Box sx={{ mb: 2 }}>
                                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                    Your Bid
                                  </Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                                    <DollarSign size={16} color="#5CE9E7" />
                                    <Typography variant="body1" sx={{ color: 'white', ml: 1, fontWeight: 500 }}>
                                      {formatAmountDisplay(amount)}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Grid>
                            </Grid>

                            <Box sx={{ 
                              pt: 2, 
                              mt: 2, 
                              borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}>
                              <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 500 }}>
                                Projected Return
                              </Typography>
                              <Typography 
                                variant="h6" 
                                sx={{ 
                                  color: '#5CE9E7', 
                                  fontWeight: 700,
                                  background: 'linear-gradient(90deg, #5CE9E7 0%, #EA6CFC 100%)',
                                  backgroundClip: 'text',
                                  WebkitBackgroundClip: 'text',
                                  WebkitTextFillColor: 'transparent'
                                }}
                              >
                                {money(projectedReturns.returns, selectedCurrencyObj?.type)}
                              </Typography>
                            </Box>
                          </CardContent>
                        </Card>
                      )}

                      {/* Confirm Button */}
                      <Button
                        onClick={submitInvestment}
                        disabled={!isAmountValid || submittingInvestment}
                        fullWidth
                        sx={{
                          py: 1.5,
                          background: isAmountValid 
                            ? 'linear-gradient(90deg, #5CE9E7 0%, #EA6CFC 100%)' 
                            : 'rgba(255, 255, 255, 0.1)',
                          color: isAmountValid ? '#050520' : 'rgba(255, 255, 255, 0.5)',
                          fontWeight: 600,
                          borderRadius: 2,
                          position: 'relative',
                          overflow: 'hidden',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            background: isAmountValid 
                              ? 'linear-gradient(90deg, #5CE9E7 20%, #EA6CFC 100%)' 
                              : 'rgba(255, 255, 255, 0.1)',
                            transform: isAmountValid ? 'translateY(-2px)' : 'none',
                            boxShadow: isAmountValid 
                              ? '0 8px 20px rgba(92, 233, 231, 0.3)' 
                              : 'none'
                          },
                          '&:before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: '-100%',
                            width: '100%',
                            height: '100%',
                            background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
                            transition: 'all 0.6s ease',
                            animation: isAmountValid ? 'shine 2s infinite' : 'none',
                            '@keyframes shine': {
                              '0%': {
                                left: '-100%',
                              },
                              '100%': {
                                left: '100%',
                              },
                            },
                          }
                        }}
                      >
                        {submittingInvestment ? (
                          <CircularProgress size={24} sx={{ color: '#050520' }} />
                        ) : (
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <span>Confirm Bid</span>
                            <ArrowRight size={18} style={{ marginLeft: '8px' }} />
                          </Box>
                        )}
                      </Button>
                    </>
                  ) : (
                    <Box 
                      sx={{ 
                        height: '100%', 
                        display: 'flex', 
                        flexDirection: 'column', 
                        justifyContent: 'center',
                        alignItems: 'center',
                        py: 8
                      }}
                    >
                      <Box 
                        sx={{ 
                          width: 80, 
                          height: 80, 
                          borderRadius: '50%',
                          background: 'rgba(255, 255, 255, 0.05)',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          mb: 3
                        }}
                      >
                        <ChevronsRight 
                          size={40} 
                          style={{ 
                            color: 'rgba(92, 233, 231, 0.7)'
                          }} 
                        />
                      </Box>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          color: 'white', 
                          textAlign: 'center', 
                          mb: 1
                        }}
                      >
                        Select a Plan to Begin
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: 'rgba(255, 255, 255, 0.7)', 
                          textAlign: 'center',
                          maxWidth: '80%'
                        }}
                      >
                        Choose a bid plan from the left to see available amounts and projections
                      </Typography>
                    </Box>
                  )}
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Container>
      </Box>
    </WrapUpP2p>
  );
};

export default NeonInvestmentFlow;